import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { RECEIPT_ACCEPT, apiError, formatDay, receiptProblem } from "../../lib/finance";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";
import { toast } from "sonner";
import { ArrowRight, FileText, Image as ImageIcon, Loader2, UploadCloud } from "lucide-react";

const INBOX_BATCH = 50; // server limit per POST /finance/inbox

export const piecePath = (id) => `/finance/depenses/nouvelle?piece=${encodeURIComponent(id)}`;

// « Justificatifs à saisir »: drop a pile of receipts at once, then enter them one by one.
// Each file waits here (never counted as an expense) until it becomes a draft through the normal form.
export default function ReceiptInbox({ inbox, onChanged }) {
  const navigate = useNavigate();
  const fileInput = useRef(null);
  const [progress, setProgress] = useState(null); // {done, total} while sending
  const [rejected, setRejected] = useState([]); // [{name, reason}]
  const [removing, setRemoving] = useState(null);
  const items = inbox.items || [];

  const send = async (list) => {
    const files = Array.from(list || []);
    if (!files.length) return;
    const refused = [];
    const valid = files.filter((f) => { const why = receiptProblem(f); if (why) refused.push({ name: f.name, reason: why }); return !why; });
    setRejected(refused);
    if (!valid.length) return;
    setProgress({ done: 0, total: valid.length });
    const ids = [];
    for (const file of valid) {
      try {
        const data = new FormData();
        data.append("file", file);
        const res = await api.post("/finance/attachments", data, { headers: { "Idempotency-Key": crypto.randomUUID() } });
        ids.push(res.data.id);
      } catch (err) {
        refused.push({ name: file.name, reason: apiError(err) });
      }
      setProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    try {
      for (let i = 0; i < ids.length; i += INBOX_BATCH) await api.post("/finance/inbox", { attachment_ids: ids.slice(i, i + INBOX_BATCH) });
      if (ids.length) toast.success(`${ids.length} justificatif${ids.length > 1 ? "s" : ""} à saisir`);
    } catch (err) {
      toast.error(apiError(err));
    }
    setRejected([...refused]);
    setProgress(null);
    onChanged();
  };

  const remove = async () => {
    const piece = removing;
    setRemoving(null);
    try {
      await api.delete(`/finance/inbox/${piece.id}`);
      toast.success("Justificatif retiré");
      onChanged();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <section className="space-y-4" aria-label="Justificatifs à saisir" data-testid="finance-inbox">
      <Card className="p-4 sm:p-5 space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h3 className="font-display font-semibold text-slate-900">Déposez vos justificatifs, saisissez-les ensuite</h3>
            <p className="text-sm text-slate-500">Tickets, factures, captures de paiement : chaque fichier attend ici, puis devient une dépense en brouillon quand vous la complétez.</p>
          </div>
          {items.length > 0 && (
            <Button className="shrink-0" onClick={() => navigate(piecePath(items[0].id))} data-testid="finance-inbox-start">
              Saisir {items.length > 1 ? `les ${items.length} justificatifs` : "le justificatif"} <ArrowRight size={15} className="ml-1.5" />
            </Button>
          )}
        </div>
        <div className="rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-center"
          onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); if (!progress) send(e.dataTransfer.files); }}>
          <UploadCloud size={22} className="mx-auto text-slate-400" aria-hidden="true" />
          <p className="text-sm text-slate-700 mt-1">Déposez plusieurs fichiers ici, ou</p>
          <Button type="button" variant="outline" size="sm" className="mt-2" disabled={!!progress} onClick={() => fileInput.current?.click()} data-testid="finance-inbox-pick">
            Choisir des fichiers ou prendre des photos
          </Button>
          <input ref={fileInput} type="file" multiple accept={RECEIPT_ACCEPT} className="sr-only" tabIndex={-1} data-testid="finance-inbox-input"
            onChange={(e) => { send(e.target.files); e.target.value = ""; }} />
          <p className="text-xs text-slate-500 mt-2">PDF ou photo (JPEG, PNG, WebP), 10 Mo maximum par fichier.</p>
        </div>
        {progress && (
          <div className="space-y-1" aria-live="polite">
            <Progress value={Math.round((progress.done / progress.total) * 100)} aria-label="Envoi des justificatifs" />
            <p className="text-xs text-slate-500 flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Envoi {progress.done}/{progress.total}…</p>
          </div>
        )}
        {rejected.length > 0 && !progress && (
          <ul className="text-sm text-red-700 space-y-0.5" role="alert" data-testid="finance-inbox-rejected">
            {rejected.map((r, i) => <li key={i}><span className="font-medium">{r.name}</span> : {r.reason}</li>)}
          </ul>
        )}
      </Card>

      {inbox.status === "error" ? (
        <Card className="p-5 text-center space-y-2">
          <p className="text-sm text-slate-600">{inbox.error}</p>
          <Button variant="outline" onClick={onChanged}>Réessayer</Button>
        </Card>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-500 px-1" data-testid="finance-inbox-empty">Aucun justificatif en attente. Ce que vous déposez apparaîtra ici.</p>
      ) : (
        <Card className="divide-y divide-slate-100" data-testid="finance-inbox-list">
          {items.map((piece) => (
            <div key={piece.id} className="flex items-center gap-3 px-3 py-2.5 text-sm" data-testid="finance-inbox-item">
              {piece.mime === "application/pdf" ? <FileText size={16} className="text-slate-400 shrink-0" aria-hidden="true" /> : <ImageIcon size={16} className="text-slate-400 shrink-0" aria-hidden="true" />}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-slate-900">{piece.name}</span>
                <span className="block text-xs text-slate-500">Déposé le {formatDay((piece.inbox_at || piece.created_at || "").slice(0, 10))}</span>
              </span>
              <Button variant="ghost" size="sm" onClick={() => setRemoving(piece)} data-testid="finance-inbox-remove">Retirer</Button>
              <Button variant="outline" size="sm" onClick={() => navigate(piecePath(piece.id))} data-testid="finance-inbox-enter">Saisir</Button>
            </div>
          ))}
        </Card>
      )}

      <AlertDialog open={!!removing} onOpenChange={(open) => !open && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retirer « {removing?.name} » ?</AlertDialogTitle>
            <AlertDialogDescription>Le fichier ne sera rattaché à aucune dépense et sera supprimé automatiquement. Retirez-le seulement s'il a été déposé par erreur ou en double.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="finance-inbox-remove-confirm">Retirer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
